import { Suspense, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { twJoin } from "tailwind-merge";

import type { UpdateEventParticipationRequest } from "@/api/bale.zod";

import { useGetProfile, useListEventInvites, useUpdateEventParticipation } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Attention } from "@/components/Attention";
import { Button, ButtonLink } from "@/components/Button";
import { Card, CardHeader, CardSubtitleHeader, CardTextContent, CardTitle } from "@/components/Card";
import { DragHandle } from "@/components/drag-columns/components/DragItem";
import { Dropdown } from "@/components/Dropdown";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { MenuItem } from "@/components/MenuItem";
import { Subtitle } from "@/components/Subtitle";
import { TextLoading } from "@/components/TextLoading";
import { Tomato } from "@/components/Tomato";

import type { EventParticipantStyle } from "./EventParticipant";

import { useCurrentEventSuspense, useEventInviteCode } from "../../../hooks/useCurrentEvent";
import { AttendeesSection } from "./AttendeesSection";
import { ManageInvitesModal } from "./ManageInvitesModal/ManageInvitesModal";
import { PendingSection } from "./PendingSection";

type AttendingState = "hosting" | "pending" | "invited" | "public-invited" | "going" | "declined" | "ignoring";

export function AttendingCard() {
  const { t } = useTranslation();

  return (
    <Suspense
      fallback={
        <Card>
          <CardSubtitleHeader>{t("event-attending-title", { current: 0, max: null })}</CardSubtitleHeader>
          <CardTextContent className="@container flex flex-col gap-4">
            <TextLoading className="h-12 w-full" />
            <div className="flex grid-cols-2 flex-col gap-2 gap-y-4 @md:grid">
              <div className="flex flex-col gap-2">
                <h3 className="font-hint">{t("event-invited", { count: 0 })}</h3>
                <TextLoading className="h-12 w-full" />
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="font-hint">{t("event-not-going", { count: 0 })}</h3>
                <TextLoading className="h-12 w-full" />
              </div>
            </div>
          </CardTextContent>
        </Card>
      }
    >
      <AttendingCardContent />
    </Suspense>
  );
}

function AttendingCardContent() {
  const { t } = useTranslation();
  const location = useLocation();

  const id = useId();
  const popover = useRef<HTMLDivElement>(null);
  const manageInvitesModal = useRef<HTMLDialogElement>(null);
  const [expandStyle, setExpandStyle] = useState<EventParticipantStyle>("full");

  const updateParticipation = useUpdateEventParticipation();
  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });

  const host = event.data.event_participants.find(
    (participant) => participant.profile.id === event.data.event.account_id,
  )!;
  const participants = event.data.event_participants.filter((participant) => participant.response === "Attending");

  const invites = useListEventInvites(event.data.event.id, {
    query: { select: (data) => data.data.event_invites },
  });
  const participantMe = me.isSuccess
    ? event.data.event_participants.find((participant) => participant.profile.id === me.data.id)
    : null;

  const inviteCode = useEventInviteCode();
  let attendingState: AttendingState = "pending";
  switch (participantMe?.response) {
    case "Attending":
      attendingState = "going";
      if (me.isSuccess && host.profile.id === me.data.id) attendingState = "hosting";
      break;
    case "NotAttending":
      attendingState = "declined";
      break;
    case "Ignored":
      attendingState = "ignoring";
      break;
    case undefined:
    case null:
    default:
      if (invites.isSuccess && me.isSuccess && invites.data.some((invite) => invite.invited.id === me.data.id)) {
        attendingState = "invited";
      } else if (!me.isSuccess && inviteCode) {
        attendingState = "public-invited";
      }
  }

  function rsvp(data: UpdateEventParticipationRequest) {
    popover.current?.hidePopover();
    updateParticipation.mutate({
      accountId: me.data!.id,
      eventId: event.data.event.id,
      data,
    });
  }

  return (
    <Card>
      <DragHandle>
        <CardHeader>
          <div className="grid grow grid-cols-[1fr_auto_1fr]">
            <CardTitle>
              <Subtitle>
                {t("event-attending-title", { current: participants.length, max: event.data.event.max_attendees })}
              </Subtitle>
            </CardTitle>
            <div className="flex flex-row items-center">{attendingState === "invited" && <Attention />}</div>
            <ActionBox className="justify-self-end">
              <IconButton
                type="button"
                name="align-justify"
                aria-pressed={expandStyle === "name-only"}
                onClick={() => setExpandStyle("name-only")}
                className={twJoin(expandStyle !== "name-only" && "text-black/20")}
              />
              <IconButton
                type="button"
                name="list"
                aria-pressed={expandStyle === "full"}
                onClick={() => setExpandStyle("full")}
                className={twJoin(expandStyle !== "full" && "text-black/20")}
              />
              <IconButton
                type="button"
                name="grid"
                aria-pressed={expandStyle === "avatar-only"}
                onClick={() => setExpandStyle("avatar-only")}
                className={twJoin(expandStyle !== "avatar-only" && "text-black/20")}
              />
            </ActionBox>
          </div>
        </CardHeader>
      </DragHandle>
      <CardTextContent className="@container flex flex-col gap-4">
        <AttendeesSection style={expandStyle} />
        {attendingState !== "public-invited" && <PendingSection style={expandStyle} />}

        <div className="flex min-w-0 flex-row gap-3">
          {attendingState === "public-invited" && (
            <ButtonLink
              className="grow"
              attention="submit"
              to={`/join?next=${location.pathname}`}
              state={{ showingForm: "signup" }}
            >
              {t("sign-up-to-respond")}
              <Tomato className="absolute -top-2 -right-2" />
            </ButtonLink>
          )}
          {attendingState !== "public-invited" && (
            <Button
              type="button"
              attention="submit"
              className="flex grow flex-row items-center gap-4 px-4"
              popoverTarget={id}
              disabled={attendingState === "hosting" || !me.isSuccess}
            >
              <div className="grow">{t("event-rsvp-state", { state: attendingState })}</div>
              {attendingState !== "hosting" && <Icon className="ml-auto" name="chevron-down" />}
              {attendingState === "invited" && <Tomato className="absolute -top-2 right-0" />}
            </Button>
          )}
          {(attendingState === "going" || attendingState === "hosting") && (
            <>
              <Button type="button" className="grow" onClick={() => manageInvitesModal.current?.showModal()}>
                {t("event-manage-sharing")}
              </Button>
              <ManageInvitesModal ref={manageInvitesModal} onClose={() => manageInvitesModal.current?.close()} />
            </>
          )}
        </div>
      </CardTextContent>

      <Dropdown
        id={id}
        popover="auto"
        className="absolute -mt-2 mr-2 min-w-[anchor-size(width)] [position-area:bottom]"
        ref={popover}
      >
        <MenuItem onClick={() => rsvp({ response: "Attending", discoverable_distance: 0 })}>{t("rsvp-going")}</MenuItem>
        <MenuItem onClick={() => rsvp({ response: "NotAttending" })}>{t("rsvp-not-going")}</MenuItem>
        <MenuItem onClick={() => rsvp({ response: "Ignore" })}>{t("rsvp-ignore")}</MenuItem>
      </Dropdown>
    </Card>
  );
}
